import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Autocomplete,
} from "@mui/material";
import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { User } from "../../models/Users/User";
import agent from "../../../api/agent";
import { jwtDecode } from "jwt-decode";
import LoadingComponent from "../../layout/share_components/LoadingComponent";
import ClearIcon from "@mui/icons-material/Clear";
import { checkTokenExpiration } from "../../../api/tokenExpirationChecking";

interface Props {
  defaultUserId: number | undefined;
  userLinkSubmit: (userId: number | undefined) => void;
  closeForm: () => void;
  userRole: string;
  subscriptionId: number;
  // licenseKey: string;
}

export default function AddOrEditUserLinkForm({
  defaultUserId,
  userLinkSubmit,
  closeForm,
  userRole,
  subscriptionId,
  // licenseKey,
}: Props) {
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(
    defaultUserId,
  );
  const [userList, setUserList] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // State for conflict dialog
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [conflictUserId, setConflictUserId] = useState<number | null>(null);

  // For filteration of the user dropdown
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredUserList, setFilteredUserList] = useState<User[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          if (checkTokenExpiration(tokenFLocal)) {
            return;
          }
          // Let super admin has the list of all users, and other roles only have the list of users in their company
          if (userRole === "Super Admin") {
            const response = await agent.User.list(tokenFLocal);

            const sortedUserList = [...response].sort((a, b) =>
              a.email.toLowerCase().localeCompare(b.email.toLowerCase()),
            );

            setUserList(sortedUserList);
            setFilteredUserList(sortedUserList);
          } else {
            const decodedToken: any = jwtDecode(tokenFLocal);
            const userId = decodedToken.sub;

            const userResponse = await agent.User.getUserDetail(
              userId,
              tokenFLocal,
            );
            const companyId = userResponse.companyId;
            const userListResponse = await agent.User.getUserByCompanyId(
              companyId,
              tokenFLocal,
            );

            const sortedUserList = [...userListResponse].sort((a, b) =>
              a.email.toLowerCase().localeCompare(b.email.toLowerCase()),
            );

            setUserList(sortedUserList);
            setFilteredUserList(sortedUserList);
          }
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (isSubmitting) return;

    if (selectedUserId === undefined) {
      setIsSubmitting(true);
      try {
        await userLinkSubmit(undefined); // Pass undefined for unassign
        closeForm();
      } catch (error) {
        console.error("Error unassigning user:", error);
        setIsSubmitting(false);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem("token");
      if (!token || checkTokenExpiration(token)) {
        return;
      }

      // Check for conflict
      const hasConflict = await agent.License.checkUserConflict(
        subscriptionId,
        selectedUserId,
        token,
      );

      if (hasConflict) {
        // Store the user ID and show confirmation dialog
        setConflictUserId(selectedUserId);
        setShowConflictDialog(true);
        setIsSubmitting(false);
      } else {
        // No conflict, proceed with linking
        await proceedWithLinking(selectedUserId);
      }
    } catch (error) {
      console.error("Error checking conflict:", error);
      setIsSubmitting(false);
    }
  }

  async function proceedWithLinking(userId: number) {
    try {
      // const token = localStorage.getItem("token");

      // Call the parent's submit function
      await userLinkSubmit(userId);

      // Close the form after successful submission
      closeForm();
    } catch (error) {
      console.error("Error linking user:", error);
    } finally {
      setIsSubmitting(false);
      setConflictUserId(null);
    }
  }

  // Handle conflict confirmation
  async function handleConflictConfirm() {
    if (conflictUserId) {
      setShowConflictDialog(false);
      setIsSubmitting(true);
      await proceedWithLinking(conflictUserId);
    }
  }

  function handleConflictCancel() {
    setShowConflictDialog(false);
    setConflictUserId(null);
    setIsSubmitting(false);
  }

  function handleUserDropdownChange(event: SelectChangeEvent<number>) {
    const value = event.target.value;
    setSelectedUserId(value === "" ? undefined : (value as number));
  }

  // Handle search term change for filtering the user list
  useEffect(() => {
    const filtered = userList.filter((user) =>
      user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUserList(filtered);
  }, [searchTerm, userList]);

  function handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    setSearchTerm(event.target.value);
  }

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;
  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        {/* user Dropdown */}
        <FormControl
          fullWidth
          margin="normal"
          sx={{ position: "relative", overflow: "visible" }}
        >
          <Autocomplete
            value={
              userList.find((user) => user.userId === selectedUserId) || null
            }
            onChange={(event, newValue) => {
              // Create a synthetic event that matches SelectChangeEvent<number>
              const syntheticEvent = {
                target: {
                  value: newValue?.userId === "" ? "" : newValue?.userId || "",
                },
              } as SelectChangeEvent<number>;

              handleUserDropdownChange(syntheticEvent);
            }}
            options={[
              // Add unassign option at the top
              {
                userId: "",
                email: "",
                isUnassignOption: true,
              },
              ...filteredUserList,
            ]}
            getOptionLabel={(option) => {
              if (option.userId === "") return "";
              return option.email;
            }}
            isOptionEqualToValue={(option, value) =>
              option.userId === value?.userId
            }
            renderOption={(props, option) => {
              if (option.userId === "") {
                return (
                  <li {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "error.main",
                      }}
                    >
                      <ClearIcon fontSize="small" sx={{ mr: 1 }} />
                      <span>Unassign User</span>
                    </Box>
                  </li>
                );
              }
              return <li {...props}>{option.email}</li>;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User Email"
                variant="outlined"
                onChange={handleSearchChange}
                value={searchTerm}
              />
            )}
            filterOptions={(options) => options}
            freeSolo={false}
            disableClearable={false}
            loading={isSubmitting}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
            }}
          />
        </FormControl>

        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={closeForm}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </Box>
      </Box>

      <Dialog open={showConflictDialog} onClose={handleConflictCancel}>
        <DialogTitle>More than one license for the same user</DialogTitle>
        <DialogContent>
          This user already has a license in this subscription. Are you sure you
          want to link another license to him/her?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConflictCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConflictConfirm}
            color="primary"
            autoFocus
            disabled={isSubmitting}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
